import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bookPlan } from '@kitup/book-schema';
import { useState, type JSX } from 'react';
import { Link } from 'react-router';
import type { BookDto } from '../api/books.js';
import { CHARACTER_KINDS, charactersApi, type CharacterDto, previewImage } from '../api/library.js';
import { EmptyState } from '../components/EmptyState.js';
import { useI18n } from '../i18n/context.js';
import { CharacterEditor } from '../library/CharacterEditor.js';
import { ActionError } from '../library/ActionError.js';
import { useActionFeedback } from '../library/useActionFeedback.js';
import { LibraryPicker } from '../library/LibraryPicker.js';
import { useCreatedItemScroll } from '../library/useCreatedItemScroll.js';

/**
 * Шаг «Герои» конструктора (E7-T2). Рендер привязан к стилю книги: смена стиля
 * потребует нового листа, а не «обновит» существующий (01-SPEC.md §3.4).
 */
export function CharactersStep({
  book,
  readOnly = false,
}: {
  book: BookDto;
  readOnly?: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  // While a card is open the empty create form stays folded: above the card it pushed it a
  // screen down on a phone, and its Name field read as the card's.
  const [adding, setAdding] = useState(false);
  const openCard = (id: string | null): void => {
    setOpenId(id);
    // Opening or closing a card folds the create form again (or leaves it open with no card).
    setAdding(false);
  };
  const action = useActionFeedback();

  const linked = useQuery({
    queryKey: ['book-characters', book.id],
    queryFn: () => charactersApi.forBook(book.id),
  });
  const library = useQuery({
    queryKey: ['characters'],
    queryFn: () => charactersApi.list(),
    enabled: !readOnly,
  });

  const items = linked.data?.items ?? [];
  const limit = bookPlan(book.plan).maxCharacters;
  const { scrollToCreatedItem, itemRef } = useCreatedItemScroll<HTMLLIElement>(
    items.map((item) => item.id),
  );
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['book-characters', book.id] });
    void queryClient.invalidateQueries({ queryKey: ['characters'] });
    void queryClient.invalidateQueries({ queryKey: ['book', book.id] });
  };

  /** Состав пересобирается целиком, поэтому отметки главных надо переносить. */
  const link = (ids: readonly string[], leads?: ReadonlySet<string>): Promise<void> => {
    const current = leads ?? new Set(items.filter((item) => item.isLead).map((item) => item.id));
    return charactersApi
      .linkToBook(
        book.id,
        ids.map((characterId, order) => ({
          characterId,
          order,
          isLead: current.has(characterId),
        })),
      )
      .then(refresh);
  };

  const toggleLead = (characterId: string): Promise<void> => {
    const leads = new Set(items.filter((item) => item.isLead).map((item) => item.id));
    if (leads.has(characterId)) leads.delete(characterId);
    else leads.add(characterId);
    return link(
      items.map((item) => item.id),
      leads,
    );
  };

  const noLeads = items.length > 0 && items.every((item) => !item.isLead);

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t.characters.stepTitle}</h1>
        <p className="mt-1 text-sm font-medium text-[var(--kitup-color-muted)]">
          {t.monetization.planLimit(items.length, limit)}
        </p>
        <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.characters.stepSubtitle}</p>
      </div>

      {readOnly || items.length >= limit ? null : openId && !adding ? (
        <button
          type="button"
          className="kitup-secondary-button justify-self-start"
          onClick={() => {
            setAdding(true);
            // The button gives way to the form: focus follows to its first field, not <body>.
            requestAnimationFrame(() =>
              document.querySelector<HTMLInputElement>('#library-picker-new-name')?.focus(),
            );
          }}
        >
          + {t.characters.addAnother}
        </button>
      ) : (
        <LibraryPicker
          addLabel={t.characters.add}
          createLabel={t.characters.create}
          nameLabel={t.characters.fields.name}
          kinds={{
            label: t.characters.fields.kind,
            options: CHARACTER_KINDS.map((kind) => ({
              value: kind,
              label: t.characters.kinds[kind],
            })),
          }}
          options={(library.data?.items ?? [])
            .filter((character) => !items.some((item) => item.id === character.id))
            .map((character) => ({
              id: character.id,
              label: character.name,
              imageUrl: previewImage(character, book.styleId),
              detail:
                character.usage.books > 0
                  ? t.library.picker.usedIn(character.usage.books)
                  : t.library.picker.notUsed,
            }))}
          onAdd={(id) => link([...items.map((item) => item.id), id]).then(() => setAdding(false))}
          onCreate={(name, kind) =>
            charactersApi.create({ name, kind: kind! }).then((created) => {
              setOpenId(created.id);
              setAdding(false);
              scrollToCreatedItem(created.id);
              return link([...items.map((item) => item.id), created.id]);
            })
          }
        />
      )}
      <ActionError message={action.error} />

      {items.length === 0 ? (
        <EmptyState
          title={t.characters.stepEmpty}
          hint={
            (library.data?.items.length ?? 0) > 0
              ? t.characters.stepEmptyHint
              : t.characters.stepEmptyHintFirst
          }
        />
      ) : null}

      {noLeads ? (
        <p role="status" className="text-sm text-[var(--kitup-color-muted)]">
          {t.characters.leadHint}
        </p>
      ) : null}

      <ul className="grid gap-3">
        {items.map((character) => (
          <li ref={itemRef(character.id)} key={character.id} className="kitup-card p-4">
            <div className="kitup-expandable-row flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {/* The approved drawing: what says "this is my child" at a glance. */}
                <CharacterThumb url={previewImage(character, book.styleId)} />
                <div className="grid gap-0.5">
                  <button
                    type="button"
                    className="kitup-expandable-trigger"
                    aria-expanded={openId === character.id}
                    onClick={() => openCard(openId === character.id ? null : character.id)}
                  >
                    <span>{character.name}</span>
                    <span className="kitup-expandable-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </button>
                  {/*
                  Status under the name, and — for a character also in another book — where,
                  so editing it here is not a surprise there.
                */}
                  <span className="text-xs text-[var(--kitup-color-muted)]">
                    {statusLabel(character, book.styleId, t.library.rendition.statuses)}
                    {character.usage.books > 1
                      ? ` · ${t.library.picker.usedIn(character.usage.books - 1)}`
                      : ''}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={character.isLead}
                    disabled={readOnly || action.pending}
                    onChange={() => action.run(toggleLead(character.id))}
                  />
                  {t.characters.lead}
                </label>
                <button
                  type="button"
                  className="text-sm text-red-700 underline-offset-2 hover:underline disabled:opacity-50"
                  disabled={readOnly || action.pending}
                  onClick={() =>
                    action.run(
                      link(items.filter((item) => item.id !== character.id).map((item) => item.id)),
                    )
                  }
                >
                  {t.library.actions.removeFromBook}
                </button>
              </div>
            </div>
            {openId === character.id ? (
              <div className="mt-4">
                <CharacterEditor
                  character={character}
                  styleId={book.styleId}
                  bookId={book.id}
                  readOnly={readOnly}
                  onChanged={refresh}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {readOnly ? null : (
        <Link
          to={`/app/characters?bookId=${encodeURIComponent(book.id)}`}
          className="justify-self-start text-sm text-[var(--kitup-color-muted)] underline underline-offset-2"
        >
          {t.library.actions.openLibrary}
        </Link>
      )}
    </div>
  );
}

function statusLabel(
  character: CharacterDto,
  styleId: string,
  labels: Record<string, string>,
): string {
  const rendition = character.renditions.find((item) => item.styleId === styleId);
  return labels[rendition?.status ?? 'empty'] ?? '';
}

/** Nothing until there is something to show: an empty square read as a broken image. */
export function CharacterThumb({ url }: { readonly url: string | undefined }): JSX.Element | null {
  return url ? (
    <img
      src={url}
      alt=""
      className="h-12 w-12 shrink-0 rounded-[var(--kitup-radius-sm)] object-cover"
    />
  ) : null;
}
